import React from 'react'
import { Link } from 'react-router-dom'
import { FiUsers, FiCalendar, FiShield, FiUser, FiMapPin, FiPlus, FiFlag } from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import AvatarBadge from './AvatarBadge'
import { formatClubDisplayName } from '../utils/clubDisplay'

function CompactGroupCard({ group }) {
  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return {
          icon: FiShield,
          text: 'Owner',
          className: 'bg-accent/10 text-accent border-accent/30'
        }
      case 'admin':
        return {
          icon: FiShield,
          text: 'Admin',
          className: 'bg-accent/10 text-accent border-accent/30'
        }
      default:
        return {
          icon: FiUser,
          text: 'Member',
          className: 'bg-surface text-text-secondary border-border-subtle'
        }
    }
  }

  const roleBadge = getRoleBadge(group.userRole)

  // Member avatar list - owner first, then alphabetical, capped at 4 with +N overflow.
  const sortedMembers = [...group.members].sort((a, b) => {
    if (a.role === 'owner') return -1
    if (b.role === 'owner') return 1
    const nameA = (typeof a === 'string' ? a : a.name) || ''
    const nameB = (typeof b === 'string' ? b : b.name) || ''
    return nameA.localeCompare(nameB)
  })

  return (
    <div className="bg-card rounded-lg border border-border-subtle p-4 sm:p-5 space-y-4 hover:border-text-muted transition-colors h-full flex flex-col">
      {/* Header: name + badges */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base font-semibold text-text-primary truncate">
            {group.name}
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center text-text-secondary bg-surface px-2 py-1 rounded text-xs font-medium border border-border-subtle">
            <SafeIcon icon={FiUsers} className="w-3 h-3 mr-1" />
            {group.members.length}
          </div>
          <div className={`flex items-center px-2 py-1 rounded text-xs font-medium border ${roleBadge.className}`}>
            <SafeIcon icon={roleBadge.icon} className="w-3 h-3 mr-1" />
            {roleBadge.text}
          </div>
        </div>
      </div>

      {/* Stats: upcoming + rounds played. Two tiles only - keeping the
          column count fixed across all cards for visual alignment. */}
      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border-subtle">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center mr-3 border border-border-subtle">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-text-secondary" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-text-primary text-sm">{group.upcomingRounds}</p>
            <p className="text-xs text-text-muted">Upcoming</p>
          </div>
        </div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center mr-3 border border-border-subtle">
            <SafeIcon icon={FiFlag} className="w-4 h-4 text-text-secondary" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-text-primary text-sm">{group.roundsPlayed ?? 0}</p>
            <p className="text-xs text-text-muted">Rounds Played</p>
          </div>
        </div>
      </div>

      {/* Home course - always rendered (placeholder when not set) so cards
          across a row align identically regardless of which groups have a
          home course configured. */}
      <div className="flex items-center">
        <div className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center mr-3 border border-border-subtle">
          <SafeIcon icon={FiMapPin} className={`w-4 h-4 ${group.home_course ? 'text-text-secondary' : 'text-text-muted'}`} />
        </div>
        <div className="min-w-0">
          <p className={`font-medium text-sm truncate ${group.home_course ? 'text-text-primary' : 'text-text-muted'}`}>
            {group.home_course ? formatClubDisplayName(group.home_course) : 'Not Specified'}
          </p>
          <p className="text-xs text-text-muted">Home Course</p>
        </div>
      </div>

      {/* Member avatars (owner first, then alpha, cap 4 + overflow chip) */}
      <div>
        <p className="text-xs font-medium text-text-muted mb-2">Members</p>
        <div className="flex items-center space-x-2">
          {sortedMembers.slice(0, 4).map((member, index) => {
            const memberName = typeof member === 'string' ? member : member.name
            const isOwner = member.role === 'owner'
            return (
              <AvatarBadge
                key={index}
                name={memberName}
                size="sm"
                owner={isOwner}
              />
            )
          })}
          {group.members.length > 4 && (
            <div className="w-8 h-8 bg-surface rounded-full flex items-center justify-center text-xs font-medium text-text-muted border border-border-subtle">
              +{group.members.length - 4}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons - New Round (primary) + View Group (secondary).
          Same pattern as the page-level button rows on Groups / Rounds. */}
      <div className="flex flex-row items-center gap-3 pt-2 mt-auto">
        <Link
          to={`/create-round/${group.id}`}
          className="flex-1 min-h-[44px] inline-flex items-center justify-center px-4 py-2.5 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-medium text-sm"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          New Round
        </Link>
        <Link
          to={`/group/${group.invite_code || group.id}`}
          className="flex-1 min-h-[44px] inline-flex items-center justify-center px-4 py-2.5 border border-border-subtle text-text-secondary rounded-md hover:bg-surface transition-colors font-medium text-sm"
        >
          View Group
        </Link>
      </div>
    </div>
  )
}

export default CompactGroupCard
